/**
 * Los roles del personal, como etiquetas reutilizables.
 *
 * Antes el rol era un campo de texto libre, uno por persona. Con dos problemas
 * que aparecen recién cuando el equipo pasa de tres: hay que volver a escribir
 * "Cardiólogo" en cada cardiólogo que se da de alta —y alcanza una letra
 * distinta para que queden dos roles que la app cree diferentes—, y una persona
 * que hace dos cosas no entra: el que es cardiólogo Y nutricionista tenía que
 * elegir uno, o escribir "Cardiólogo y nutricionista", que es un tercer rol que
 * no se parece a ninguno de los dos.
 *
 * Ahora cada persona tiene una LISTA de roles, y los que ya se usaron quedan
 * como etiquetas para tocar en vez de escribir.
 *
 *
 * NO HAY UNA LISTA DE ETIQUETAS GUARDADA EN NINGÚN LADO
 *
 * El catálogo se calcula: es la unión de los roles que el equipo ya tiene
 * puestos más los sugeridos del rubro. No hay una colección de "roles del
 * negocio" que mantener sincronizada, y por lo tanto no puede quedar
 * desactualizada ni llenarse de etiquetas que nadie usa: si la última persona
 * con ese rol se borra, la etiqueta desaparece sola.
 */

/**
 * El rol reducido a lo que lo hace distinto de otro.
 *
 * Se ignoran mayúsculas, tildes y espacios de más, por lo mismo que en los
 * nombres de servicio (lib/servicios.js): "cardiologo" escrito apurado desde el
 * teléfono y "Cardiólogo" son el mismo puesto, y si contaran como dos, el atajo
 * no sirve para nada — que es justo lo que se está tratando de arreglar.
 */
export const normalizarRol = (rol) =>
  String(rol ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

/**
 * Los roles de una persona, siempre como lista.
 *
 * Lee las dos formas porque las dos están en los datos: `roles` es lo nuevo y
 * `rol` es el campo viejo de texto suelto, que siguen teniendo los equipos
 * cargados antes de este cambio. Sin este puente, a esos negocios se les
 * vaciaba la columna de roles de un día para el otro.
 */
export const rolesDe = (persona) => {
  if (Array.isArray(persona?.roles)) {
    return persona.roles.map((r) => String(r).trim()).filter(Boolean);
  }
  const viejo = String(persona?.rol ?? '').trim();
  return viejo ? [viejo] : [];
};

/**
 * Une listas de roles dejando uno solo por cada rol repetido.
 *
 * Gana la primera forma que aparece: si el equipo ya tiene "Cardiólogo" escrito
 * con tilde, el sugerido del rubro no lo duplica sin ella. Por eso el orden en
 * que se pasan las listas importa — lo que el negocio ya usa va primero, porque
 * es como decidió escribirlo.
 */
export const unirRoles = (...listas) => {
  const vistos = new Set();
  const salida = [];

  for (const lista of listas) {
    for (const rol of lista || []) {
      const texto = String(rol ?? '').trim();
      if (!texto) continue;

      const clave = normalizarRol(texto);
      if (!clave || vistos.has(clave)) continue;

      vistos.add(clave);
      salida.push(texto);
    }
  }

  return salida;
};

/**
 * Las etiquetas para ofrecer, en el orden en que conviene mostrarlas.
 *
 * Primero las que el equipo ya usa —son las que más chance tienen de volver a
 * usarse, porque describen a este negocio y no al rubro entero— y después las
 * sugeridas del rubro que todavía no estén.
 */
export const catalogoDeRoles = (staff = [], sugeridos = []) =>
  unirRoles((staff || []).flatMap(rolesDe), sugeridos);

/** Prende o apaga un rol en la lista de una persona. */
export const alternarRol = (roles = [], rol) => {
  const clave = normalizarRol(rol);
  if (!clave) return roles;

  return roles.some((r) => normalizarRol(r) === clave)
    ? roles.filter((r) => normalizarRol(r) !== clave)
    : [...roles, String(rol).trim()];
};

/** A cuánta gente del equipo le está puesto este rol. */
export const cuantosUsanElRol = (staff = [], rol) => {
  const clave = normalizarRol(rol);
  if (!clave) return 0;
  return (staff || []).filter((p) => rolesDe(p).some((r) => normalizarRol(r) === clave)).length;
};

/**
 * El equipo sin ese rol, en nadie.
 *
 * Borrar una etiqueta es esto y no otra cosa: como el catálogo se calcula a
 * partir de los roles que el equipo tiene puestos, sacárselo a todos es lo que
 * hace que la etiqueta desaparezca. No hay una lista de etiquetas de la que
 * borrarla.
 *
 * Se recalcula también el campo viejo `rol` de cada persona, porque si no
 * quedaría nombrando un rol que la lista nueva ya no tiene.
 */
export const quitarRolDelEquipo = (staff = [], rol) => {
  const clave = normalizarRol(rol);
  if (!clave) return staff;

  return (staff || []).map((persona) => {
    const quedan = rolesDe(persona).filter((r) => normalizarRol(r) !== clave);
    if (quedan.length === rolesDe(persona).length) return persona;
    return { ...persona, roles: quedan, rol: rolParaGuardar(quedan) };
  });
};

/**
 * Lo que se guarda en el campo viejo `rol`.
 *
 * Se sigue escribiendo aunque ya nadie de esta pantalla lo lea: es un documento
 * compartido con el backend y con el bot de WhatsApp, y dejar de escribirlo
 * significa que cualquier cosa que todavía lo mire empiece a ver vacío sin que
 * nos enteremos. Cuesta una línea y evita esa clase de sorpresa.
 */
export const rolParaGuardar = (roles = []) => roles.filter(Boolean).join(', ');
